import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { reviewerApi } from "@/lib/api";
import {
  hasRequiredIdvToShow,
  isUnderRequiredIdvReview,
  requiredIdvUnmetLine,
  REQUIRED_IDV_REVIEW_LABEL,
  type RequiredIdv,
} from "@/lib/requiredIdv";

/**
 * platform#595 — the case-page card for a required ID check that went unmet,
 * and the two exception-path actions the founders decided on: offer the check
 * again, or record a manual review with a reason.
 *
 * Neither action decides the case or moves it out of review by itself. The
 * decision stays the existing Proceed / Not Verified controls on this page.
 * A manual review is also how an operator records that they confirmed identity
 * some other way: it is written down in their words, never counted as evidence.
 */

const REASON_MAX = 2000;

export interface RequiredIdvReviewCardProps {
  inquiryId: string;
  requiredIdv: RequiredIdv | null;
}

export function RequiredIdvReviewCard({ inquiryId, requiredIdv }: RequiredIdvReviewCardProps) {
  const queryClient = useQueryClient();
  const [retryOpen, setRetryOpen] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState<"retry" | "review" | null>(null);

  const underReview = isUnderRequiredIdvReview(requiredIdv);
  const unmetLine = requiredIdvUnmetLine(requiredIdv);
  if (!requiredIdv || !hasRequiredIdvToShow(requiredIdv)) return null;

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ["reviewer", "case-review", inquiryId] });
    queryClient.invalidateQueries({ queryKey: ["reviewer", "cases"] });
    queryClient.invalidateQueries({ queryKey: ["reviewer", "sessions"] });
    queryClient.invalidateQueries({ queryKey: ["reviewer", "notes", inquiryId] });
  };

  const handleRetry = async () => {
    setBusy("retry");
    try {
      const res = await reviewerApi.retryRequiredIdv(inquiryId);
      toast.success(
        res.email_sent
          ? "We emailed them a link that opens straight to the ID check."
          : "The ID check is open again. The email did not go out, so their existing link will take them there.",
      );
      setRetryOpen(false);
      refresh();
    } catch (e) {
      toast.error((e as Error).message ?? "Could not offer the ID check again.");
    } finally {
      setBusy(null);
    }
  };

  const handleReview = async () => {
    const text = reason.trim();
    if (!text) return;
    setBusy("review");
    try {
      await reviewerApi.recordRequiredIdvManualReview(inquiryId, text.slice(0, REASON_MAX));
      toast.success("Manual review recorded.");
      setReviewOpen(false);
      setReason("");
      refresh();
    } catch (e) {
      toast.error((e as Error).message ?? "Could not record the review.");
    } finally {
      setBusy(null);
    }
  };

  if (!underReview) {
    return (
      <div className="rounded-lg border border-border bg-card p-3 text-sm text-muted-foreground">
        {unmetLine}
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-amber-300 bg-amber-50/60 p-4">
      <div className="flex items-start gap-3">
        <ShieldAlert className="h-5 w-5 shrink-0 text-amber-700 mt-0.5" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-amber-900">{REQUIRED_IDV_REVIEW_LABEL}</div>
          {requiredIdv.reason && <p className="mt-1 text-sm text-foreground">{requiredIdv.reason}</p>}
          <p className="mt-2 text-xs text-muted-foreground">
            The system does not clear this on its own. Extra vouches do not replace the check,
            though you can weigh them in a manual review.
          </p>

          {requiredIdv.exceptionHistory.length > 0 && (
            <ul className="mt-3 space-y-1.5 text-sm">
              {requiredIdv.exceptionHistory.map((h, i) => (
                <li key={`${h.at}-${i}`} className="text-muted-foreground">
                  <span className="text-foreground">
                    {new Date(h.at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                  </span>{" "}
                  {h.action === "retry" ? (
                    "You offered the ID check again."
                  ) : (
                    <>
                      Manual review: <span className="text-foreground">{h.reason}</span>
                    </>
                  )}
                </li>
              ))}
            </ul>
          )}

          <div className="mt-3 flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={() => setRetryOpen(true)} disabled={busy !== null}>
              Offer the ID check again
            </Button>
            <Button size="sm" variant="outline" onClick={() => setReviewOpen(true)} disabled={busy !== null}>
              Record a manual review
            </Button>
          </div>
        </div>
      </div>

      <Dialog open={retryOpen} onOpenChange={setRetryOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Offer the ID check again</DialogTitle>
            <DialogDescription>
              We will email them a new link that opens straight to the ID check. Nothing else they
              did needs to be redone, and they can still choose not to do it. The case goes back to
              them until they submit again.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRetryOpen(false)} disabled={busy !== null}>
              Cancel
            </Button>
            <Button onClick={handleRetry} disabled={busy !== null}>
              {busy === "retry" ? "Sending…" : "Send the link"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record a manual review</DialogTitle>
            <DialogDescription>
              Say what you reviewed and what you concluded, for example how you confirmed identity
              another way. This is kept on the case. It does not mark the ID check as done, and your
              decision is still yours to record.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={REASON_MAX}
            rows={4}
            aria-label="What you reviewed and concluded"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setReviewOpen(false)} disabled={busy !== null}>
              Cancel
            </Button>
            <Button onClick={handleReview} disabled={busy !== null || reason.trim().length === 0}>
              {busy === "review" ? "Saving…" : "Save review"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
